import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as styles from "./EmojiPicker.css.js";
import { usePortalContainer, useResolvedTheme } from "../theme/ThemeProvider.js";
// Pre-pick default (new templates start with this before the user opens the picker).
export const TEMPLATE_EMOJIS = [
    "🎒", "🧳", "👕", "💊", "🔌", "📄", "🍫", "🎿",
    "🏕️", "🥾", "🧴", "✈️", "🕶️", "📷", "🧭", "🩴",
];
// emoji-mart's dataset doesn't tag 🤿 with "scuba" (only "sport"/"ocean") even though
// that's the emoji's common name — add it so the search a user actually types works.
const patchSearchKeywords = (data) => {
    data.emojis["diving_mask"]?.keywords?.push("scuba");
    return data;
};
// Both packages are only needed once the panel opens — loaded lazily and cached.
let emojiMartPromise = null;
const loadEmojiMart = () => {
    emojiMartPromise ??= Promise.all([import("@emoji-mart/react"), import("@emoji-mart/data")]).then(([reactModule, dataModule]) => ({
        Picker: reactModule.default,
        data: patchSearchKeywords(dataModule.default),
    }));
    return emojiMartPromise;
};
// The template card's glyph: a swatch button that opens a searchable emoji-mart panel.
export const EmojiPicker = ({ value, onChange, canEdit }) => {
    const [open, setOpen] = useState(null);
    const [mart, setMart] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const resolvedTheme = useResolvedTheme();
    const portalContainer = usePortalContainer();
    useEffect(() => {
        if (open === null)
            return undefined;
        let cancelled = false;
        void loadEmojiMart().then((loaded) => {
            if (!cancelled)
                setMart(loaded);
        });
        const onDocMouseDown = (e) => {
            const target = e.target;
            if (panelRef.current?.contains(target) === true)
                return;
            if (triggerRef.current?.contains(target) === true)
                return;
            setOpen(null);
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                setOpen(null);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            cancelled = true;
            document.removeEventListener("mousedown", onDocMouseDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);
    const toggle = () => {
        if (open !== null) {
            setOpen(null);
            return;
        }
        const r = triggerRef.current?.getBoundingClientRect();
        if (r !== undefined)
            setOpen({ top: r.bottom + 4, left: r.left });
    };
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: triggerRef, type: "button", className: styles.trigger, "aria-haspopup": "dialog", "aria-expanded": open !== null, "aria-label": `Template icon: ${value}. Change icon.`, disabled: !canEdit, onClick: toggle, children: value }), open !== null && createPortal(_jsx("div", { ref: panelRef, className: styles.panel, style: { top: open.top, left: open.left }, role: "dialog", "aria-label": "Choose template icon", children: mart !== null ? (_jsx(mart.Picker, { data: mart.data, theme: resolvedTheme, previewPosition: "none", skinTonePosition: "none", onEmojiSelect: (emoji) => {
                        onChange(emoji.native);
                        setOpen(null);
                    } })) : (_jsx("div", { className: styles.loading, children: "Loading\u2026" })) }), portalContainer ?? document.body)] }));
};
//# sourceMappingURL=EmojiPicker.js.map